import React, { lazy } from 'react';
import { Redirect } from 'react-router-dom';
import * as path from '../../../config/path';

const { publicPath } = path;

const routes = [
  {
    exact: true,
    path: `${publicPath}`,
    type: 'login',
    component: lazy(() =>
      import(/* webpackChunkName: "hostpage-dashboard" */ '../NewDashboard'),
    ),
  },
  {
    // SKU create/edit use the dedicated full-page NewProduct component, under the
    // migrated products URL scheme (/data/entities/products/sku/create and
    // /data/entities/products/sku/:id/edit — keyed by the numeric entity id,
    // matching the view URL). These MUST be listed before the generic
    // `/data/entities/:entityKey(products|locations)/:subEntity/:id?/:action?` route
    // below so they win the <Switch> match and render NewProduct instead of the
    // Entities tab.
    // The other sku URLs (/sku/list, /sku/:id/view) fall through to the Entities tab.
    exact: true,
    path: `${publicPath}/data/entities/products/sku/create`,
    type: 'products',
    component: lazy(() =>
      import(/* webpackChunkName: "hostpage-product" */ '../NewProduct'),
    ),
  },
  {
    exact: true,
    path: `${publicPath}/data/entities/products/sku/:id/edit`,
    type: 'products',
    component: lazy(() =>
      import(/* webpackChunkName: "hostpage-product" */ '../NewProduct'),
    ),
  },
  {
    // Back-compat: SKU create/edit originally lived at the old /products/* URLs, then
    // moved to the entities action-URL scheme (#476), and now live under the migrated
    // /data/entities/products scheme. No in-app references remain, but external
    // bookmarks / saved links to the old URLs would otherwise fall through to
    // NotFound — keep lightweight redirects to the current create URL.
    exact: true,
    path: `${publicPath}/products/new`,
    type: 'products',
    component: () => (
      <Redirect to={`${publicPath}/data/entities/products/sku/create`} />
    ),
  },
  {
    // The old edit URL keyed by the sku *code*; the new edit URL keys by the
    // numeric entity id, which can't be reconstructed from a code without a
    // lookup. Land on the SKU listing so an old edit bookmark degrades gracefully
    // (the user can find and reopen the product) instead of 404-ing.
    exact: true,
    path: `${publicPath}/products/edit/:sku`,
    type: 'products',
    component: () => (
      <Redirect to={`${publicPath}/data/entities/products/sku/list`} />
    ),
  },
  {
    // Retired standalone Customer status page — its nav entry is gone, so the
    // shell falls through to this router. Send old links (legacy Settings
    // menu, bookmarks) to the embedded Customers tab. Deliberately a FULL page
    // load (location.replace), not a client-side <Redirect>: during a
    // client-side hop mfeCssScope can flip link.disabled on the embedded
    // remote's stylesheets while they are still downloading — Chrome cancels
    // the fetch and never retries on re-enable, leaving the tab unstyled
    // until a manual refresh. `replace` keeps the old URL out of history so
    // Back does not bounce through the redirect again.
    exact: true,
    path: '/customer-status-configuration/ui',
    type: 'entities',
    component: () => {
      window.location.replace(
        `${publicPath}/data/entities/customers/customer-status`,
      );
      return null;
    },
  },
  {
    exact: true,
    path: `${publicPath}/extensions/index`,
    type: 'extensions',
    component: lazy(() =>
      import(/* webpackChunkName: "hostpage-extensions" */ '../Extensions'),
    ),
  },
  {
    exact: true,
    path: `${publicPath}/extensions/details`,
    type: 'extension',
    component: lazy(() =>
      import(/* webpackChunkName: "hostpage-extension-view" */ '../ExtensionView'),
    ),
  },
  {
    // Bulk upload flow. MUST precede the generic
    // `/data/entities/:entityKey/:subEntity/:id?/:action?` route below, or
    // "bulk" is captured as an `:id` and renders the Entities tab instead
    // (same ordering reason as the NewProduct routes above).
    //
    // Two paths: without `:mode` the page starts on the Add/Update chooser;
    // with it the chooser is skipped, so a list can link straight into a mode.
    exact: true,
    path: `${publicPath}/data/entities/:entityKey/:subEntity/bulk/:mode?`,
    type: 'entities',
    component: lazy(() =>
      import(/* webpackChunkName: "hostpage-bulk-upload" */ '../BulkUpload'),
    ),
  },
  {
    // Entities landing (the entity cards). The nav's L2 "Entities" item points at
    // /data/entities, so the section root lives here.
    exact: true,
    path: `${publicPath}/data/entities`,
    type: 'entities',
    component: lazy(() =>
      import(/* webpackChunkName: "hostpage-entities" */ '../Entities'),
    ),
  },
  {
    // Every entity type (products, locations, customers) now lives under
    // /data/entities, so this matches them all — no `:entityKey` constraint is
    // needed any more. The Entities page resolves the entity from the param.
    exact: true,
    path: `${publicPath}/data/entities/:entityKey`,
    type: 'entities',
    component: lazy(() =>
      import(/* webpackChunkName: "hostpage-entities" */ '../Entities'),
    ),
  },
  {
    // List (`/data/entities/<entity>/:subEntity`) and detail
    // (`/data/entities/<entity>/:subEntity/:id`) share a single route so the
    // optional `:id` toggles within the SAME <Switch> child. Splitting them keys
    // the Switch by a different `path`, which unmounts/remounts Entities →
    // EntityTypeDetail → the list on every row click — discarding the
    // just-selected record and flashing the listing before the detail view
    // re-resolves from the URL.
    //
    // The optional trailing `:action` supports the explicit list/view/edit scheme
    // (e.g. `/data/entities/products/sku/list`,
    // `/data/entities/locations/store/:id/view`,
    // `/data/entities/customers/label/:id/edit`). Other sub-entities omit it and
    // keep `/data/entities/:entityKey/:subEntity/:id`.
    exact: true,
    path: `${publicPath}/data/entities/:entityKey/:subEntity/:id?/:action?`,
    type: 'entities',
    component: lazy(() =>
      import(/* webpackChunkName: "hostpage-entities" */ '../Entities'),
    ),
  },
  // The whole entities section was hard-moved to /data/entities (no redirects).
  // The old /entities URLs are deliberately absent from this table: nothing
  // matches them, so the pathless <RenderRoute component={NotFoundPage} /> that
  // closes the NavigationBar <Switch> serves them a 404. NOTE: the nav config
  // must point at the /data/entities URLs for every entity card, or the card
  // lands on that 404.
  {
    exact: true,
    path: `${publicPath}/accessForbidden`,
    type: 'authenticationFlow',
    component: lazy(() =>
      import(/* webpackChunkName: "hostpage-access-forbidden" */ '../AccessForbidden'),
    ),
  },
];

export default routes;
